import { ReactNode, useRef, useState } from "react";

type DropzoneProps = {
  onFilesDropped: (files: File[]) => void;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
};

export function Dropzone({
  onFilesDropped,
  onClick,
  disabled = false,
  children,
}: DropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const counter = useRef(0);

  const reset = () => {
    counter.current = 0;
    setIsDragging(false);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    counter.current += 1;
    if (counter.current === 1) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    counter.current = Math.max(0, counter.current - 1);
    if (counter.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    reset();
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) onFilesDropped(files);
  };

  const stateClasses = isDragging
    ? "border-brand-primary bg-brand-primary/10 scale-[1.01]"
    : "border-gray-400 bg-surface-primary hover:border-brand-primary hover:bg-brand-primary/5";

  return (
    <div
      onClick={disabled ? undefined : onClick}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`w-full max-w-2xl mx-auto rounded-xl border-2 border-dashed p-10 transition-all cursor-pointer ${stateClasses} ${
        disabled ? "opacity-60 cursor-not-allowed" : ""
      }`}
    >
      {children}
    </div>
  );
}
